import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { Subscription } from 'rxjs';

import { DateTime } from 'luxon';

import { ActivityBlockingService } from '../services/activity-blocking.service';
import { AuthService } from '../authentication/auth.service';
import { BrowserProviderService } from '../services/browser-provider.service';
import { SharedSettings } from '../models/shared-settings';
import { ToastMessageOptions } from 'primeng/api';
import { MessageService } from '../services/message-service';

@Component({
    selector: 'theme-root',
    templateUrl: './theme.component.html',
    standalone: false
})
export class ThemeComponent implements OnInit, OnDestroy {

    @Input() buildNumber: string;

    menuVisible: boolean = true;
    menuActive: boolean = true;

    resetMenu: boolean;

    showLoadingOverlay: boolean;

    authMessage: { summary: string, detail: string} = null;

    subscriptions: Subscription[];

    showDebugOptions = false;
    settingsForm = new FormGroup({
        isPropak: new FormControl(false, { nonNullable: true }),
        isTestEnvironment: new FormControl(false, { nonNullable: true }),
        expanded: new FormControl(true, { nonNullable: true })
    });

    get isPropak(): boolean {
        return this.settingsForm.controls.isPropak.value;
    }

    get isTestEnvironment(): boolean {
        return this.settingsForm.controls.isTestEnvironment.value;
    }

    // NOTE(nicholas-ramsey): April Fool's joke
    get showJarvisForklift() {
        const now = DateTime.local();

        return now.month === 4 && now.day === 1;
    }

    constructor(
        private activityService: ActivityBlockingService,
        private authService: AuthService,
        private settings: SharedSettings,
        private messageService: MessageService,
        private browser: BrowserProviderService) {

        window["toggleDebug"] = () => {
            this.showDebugOptions = !this.showDebugOptions;

            if (!this.showDebugOptions) {
                localStorage.removeItem('debugsettings');

                this.settingsForm.patchValue({
                    isPropak: this.browser.GetLocation().hostname.endsWith('propak.com'),
                    isTestEnvironment: !this.settings.isProduction,
                    expanded: true
                });
            }
        }

        window["showToast"] = (msg: ToastMessageOptions) => this.messageService.add(msg);

        this.showDebugOptions = this.browser.GetLocation().hostname === 'localhost';

        const isPropak = this.browser.GetLocation().hostname.endsWith('propak.com');
        window.document.body.classList.toggle('theme-propak', isPropak);

        this.settingsForm.patchValue({
            isPropak: isPropak,
            isTestEnvironment: !this.settings.isProduction,
            expanded: true
        });

        if (this.showDebugOptions && localStorage.getItem('debugsettings')) {
            const settings = JSON.parse(localStorage.getItem('debugsettings'));

            this.settingsForm.patchValue({
                isPropak: settings.isPropak,
                isTestEnvironment: settings.isTestEnvironment,
                expanded: settings.expanded
            });

            window.document.body.classList.toggle('theme-propak', settings.isPropak);
        }
    }

    ngOnInit(): void {
        this.subscriptions = [
            this.activityService.IsBusy.subscribe(busy => {
                setTimeout(() => { this.showLoadingOverlay = busy; });
            }),

            this.authService.isAuthenticated().subscribe(authenticated => {
                this.menuVisible = authenticated;
            }),

            this.authService.AuthMessage.subscribe(msg => {
                setTimeout(() => { this.authMessage = msg; });
            }),

            this.settingsForm.valueChanges.subscribe(val => {
                localStorage.setItem('debugsettings', JSON.stringify(val));

                window.document.body.classList.toggle('theme-propak', val.isPropak);
            })
        ];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => { s.unsubscribe(); });
    }

    toggleMenu(): void {
        this.menuActive = !this.menuActive;
    }

    toggleOverlay(): void {
        this.showLoadingOverlay = !this.showLoadingOverlay;
    }

    isMobile(): boolean {
        return window.innerWidth < 1025;
    }

    isDesktop(): boolean {
        return window.innerWidth > 1024;
    }

    isTablet(): boolean {
        const width = window.innerWidth;
        return width <= 1024 && width > 640;
    }

    onCloseMessage(event: { message: ToastMessageOptions }): void {
        this.messageService.removeMessage(event.message);
    }
}
